'use client';

import { c as _c } from "react/compiler-runtime";
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as React from 'react';
import { cn } from '../../../shared/cn';
import { sliderStyles } from './sliderStyles';
type SliderProps = ({
  variant?: 'single' | 'double';
  color?: never;
} | {
  variant: 'none';
  color?: keyof typeof sliderStyles;
}) & {
  className?: string;
  thumbLabels?: readonly [string, string?];
  thumbValueTexts?: readonly [string, string?];
} & React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>;
const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(function Slider(t0, ref) {
  const $ = _c(43);
  let ariaLabel;
  let ariaLabelledBy;
  let className;
  let color;
  let props;
  let t1;
  let thumbLabels;
  let thumbValueTexts;
  if ($[0] !== t0) {
    ({
      variant: t1,
      color,
      className,
      thumbLabels,
      thumbValueTexts,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = ariaLabelledBy;
    $[3] = className;
    $[4] = color;
    $[5] = props;
    $[6] = t1;
    $[7] = thumbLabels;
    $[8] = thumbValueTexts;
  } else {
    ariaLabel = $[1];
    ariaLabelledBy = $[2];
    className = $[3];
    color = $[4];
    props = $[5];
    t1 = $[6];
    thumbLabels = $[7];
    thumbValueTexts = $[8];
  }
  const variant = t1 === undefined ? "single" : t1;
  const effectiveColor = variant === "single" || variant === "double" ? "algoprimary" : color ?? "algoprimary";
  let t2;
  if ($[9] !== className) {
    t2 = cn("relative flex w-full touch-none select-none items-center", className);
    $[9] = className;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = variant === "none" ? ariaLabel : undefined;
  const t4 = variant === "none" ? ariaLabelledBy : undefined;
  const t5 = sliderStyles[effectiveColor];
  let t6;
  if ($[11] !== t5.track) {
    t6 = cn("relative h-2 w-full grow overflow-hidden rounded-full", t5.track);
    $[11] = t5.track;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = sliderStyles[effectiveColor];
  let t8;
  if ($[13] !== t7.range) {
    t8 = cn("absolute h-full rounded-full", t7.range);
    $[13] = t7.range;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <SliderPrimitive.Range className={t8} />;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <SliderPrimitive.Track className={t6}>{t9}</SliderPrimitive.Track>;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== ariaLabel || $[21] !== ariaLabelledBy || $[22] !== effectiveColor || $[23] !== thumbLabels?.[0] || $[24] !== thumbValueTexts?.[0] || $[25] !== variant) {
    t11 = variant !== "none" && <SliderPrimitive.Thumb aria-label={thumbLabels?.[0] ?? ariaLabel} aria-labelledby={ariaLabelledBy} aria-valuetext={thumbValueTexts?.[0]} className={cn("block h-6 w-6 rounded-full border-2 shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-backgroundInverseTertiary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:h-4 sm:w-4", sliderStyles[effectiveColor].thumb)} />;
    $[20] = ariaLabel;
    $[21] = ariaLabelledBy;
    $[22] = effectiveColor;
    $[23] = thumbLabels?.[0];
    $[24] = thumbValueTexts?.[0];
    $[25] = variant;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== ariaLabel || $[28] !== ariaLabelledBy || $[29] !== effectiveColor || $[30] !== thumbLabels?.[1] || $[31] !== thumbValueTexts?.[1] || $[32] !== variant) {
    t12 = variant === "double" && <SliderPrimitive.Thumb aria-label={thumbLabels?.[1] ?? ariaLabel} aria-labelledby={ariaLabelledBy} aria-valuetext={thumbValueTexts?.[1]} className={cn("block h-6 w-6 rounded-full border-2 shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-backgroundInverseTertiary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:h-4 sm:w-4", sliderStyles[effectiveColor].thumb)} />;
    $[27] = ariaLabel;
    $[28] = ariaLabelledBy;
    $[29] = effectiveColor;
    $[30] = thumbLabels?.[1];
    $[31] = thumbValueTexts?.[1];
    $[32] = variant;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== props || $[35] !== ref || $[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t2 || $[40] !== t3 || $[41] !== t4) {
    t13 = <SliderPrimitive.Root data-slot="control" ref={ref} className={t2} aria-label={t3} aria-labelledby={t4} {...props}>{t10}{t11}{t12}</SliderPrimitive.Root>;
    $[34] = props;
    $[35] = ref;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t2;
    $[40] = t3;
    $[41] = t4;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
});
Slider.displayName = SliderPrimitive.Root.displayName;
export { Slider };